import * as io from "io-ts";
import React from "react";
import { colors } from "@ui/Theme";
import createGlobalState from "@ui/lib/createGlobalState";
import type { NonEmptyArray } from "diagnostics-lib/array";
import { comparatorBy } from "diagnostics-lib/comparator";
import { useQuantityIndex, type Quantity } from "../../api/useQuantities";

function notNull<TValue>(value: TValue | null): value is TValue {
  return value !== null;
}

const QUANTITY_COLORS = [
  colors.raw.lightNitrogen600,
  "#00B3A4",
  "#D99E0B",
  "#D13913",
  "#8F398F",
  "#9BBF30",
  "#DB2C6F",
  "#7157D9",
  "#29A634",
  "#96622D",
] as const;

export const QUANTITY_COLOR_INDEX = io.union([
  io.literal(0),
  io.literal(1),
  io.literal(2),
  io.literal(3),
  io.literal(4),
  io.literal(5),
  io.literal(6),
  io.literal(7),
  io.literal(8),
  io.literal(9),
]);

if (QUANTITY_COLOR_INDEX.types.length !== QUANTITY_COLORS.length)
  throw new Error("Length mismatch");

export type QUANTITY_COLOR_INDEX = io.TypeOf<typeof QUANTITY_COLOR_INDEX>;
export const QUANTITY_COLOR_INDICES = QUANTITY_COLOR_INDEX.types.map(t => t.value);
export const MAX_SELECTED_QUANTITIES = QUANTITY_COLORS.length;
export const quantityColor = (colorIndex: QUANTITY_COLOR_INDEX) => QUANTITY_COLORS[colorIndex];

const SelectedQuantityId = io.strict({
  id: io.string,
  colorIndex: QUANTITY_COLOR_INDEX,
});
type SelectedQuantityId = io.TypeOf<typeof SelectedQuantityId>;

export interface SelectedQuantity {
  quantity: Quantity;
  colorIndex: QUANTITY_COLOR_INDEX;
}

export type SelectedQuantities = NonEmptyArray<SelectedQuantity>;

// store ids instead of full Quantities so that they can be shared accross setups of the same project
export const useSelectedQuantityIds = createGlobalState({
  storage: "session",
  key: "quantities",
  type: io.array(SelectedQuantityId),
  defaultValue: [],
  scoped: true,
});

function useSelectedQuantities(
  projectId: string,
): readonly [SelectedQuantity[], (newQuantities: Quantity[]) => void] {
  const quantityIndex = useQuantityIndex(projectId);
  const [selectedQuantityIds, setSelectedQuantityIds] = useSelectedQuantityIds({
    scope: projectId,
  });

  const selectedQuantities = React.useMemo(
    () =>
      selectedQuantityIds
        .map(({ colorIndex, id }) => {
          const quantity = quantityIndex[id];
          return quantity ? { colorIndex, quantity } : null;
        })
        .filter(notNull),
    [quantityIndex, selectedQuantityIds],
  );

  const defaultSelectedQuantities: SelectedQuantity[] = React.useMemo(() => {
    const sortedQuantities = Object.values(quantityIndex).sort(comparatorBy("name"));

    return sortedQuantities[0]
      ? [
          {
            quantity: sortedQuantities.find(q => q.qoiOrder === 1) ?? sortedQuantities[0],

            colorIndex: 0,
          },
        ]
      : [];
  }, [quantityIndex]);

  const quantities: SelectedQuantity[] = selectedQuantities.length
    ? selectedQuantities
    : defaultSelectedQuantities;

  const setQuantities = React.useCallback(
    (newQuantities: { id: string; name: string }[]) => {
      if (newQuantities.length > MAX_SELECTED_QUANTITIES || newQuantities.length < 1) return;
      const currentColors = quantities.reduce<{
        [key: string]: QUANTITY_COLOR_INDEX;
      }>((acc, selectedQuantity) => {
        if (newQuantities.find(newQuantity => newQuantity.name === selectedQuantity.quantity.name))
          acc[selectedQuantity.quantity.name] = selectedQuantity.colorIndex;
        return acc;
      }, {});
      const usedColorsIndices = Object.values(currentColors);
      const availableColorIndices = QUANTITY_COLOR_INDICES.filter(
        c => !usedColorsIndices.includes(c),
      );

      const newSelectedQuantities = newQuantities.map(q => ({
        id: q.id,
        colorIndex:
          newQuantities.length > 1
            ? (currentColors[q.name] ?? availableColorIndices.shift() ?? 0)
            : 0,
      }));

      setSelectedQuantityIds(newSelectedQuantities);
    },
    [quantities, setSelectedQuantityIds],
  );

  return [quantities, setQuantities] as const;
}

export default useSelectedQuantities;
